/**
 * Dashboard Config API Service
 * CRUD for saved dashboard configuration items.
 * Supports multi-role create/update and legacy single-role reads.
 */

import { apiClient } from '@/services/core/apiClient';
import type {
  DashboardItem,
  CreateDashboardItemRequest,
  UpdateDashboardItemRequest,
  MutateDashboardItemResponse,
} from '@/types/dashboardConfig';

const base = (loginUserId: string | number) =>
  `/api/ngpep/${loginUserId}/dashboard-items`;

// ─── Create ───────────────────────────────────────────────────────────────────

export async function createDashboardItem(
  loginUserId: string | number,
  token: string,
  payload: CreateDashboardItemRequest
): Promise<MutateDashboardItemResponse> {
  const response = await apiClient.post<{ success: boolean; id: number }>(
    base(loginUserId),
    payload,
    token
  );

  if (!response.success) {
    return { success: false, error: response.error ?? 'Failed to create dashboard config' };
  }

  return { success: true, id: response.data?.id };
}

// ─── Update ───────────────────────────────────────────────────────────────────

export async function updateDashboardItem(
  loginUserId: string | number,
  token: string,
  itemId: number,
  payload: Omit<UpdateDashboardItemRequest, 'id'>
): Promise<MutateDashboardItemResponse> {
  const endpoint = `${base(loginUserId)}/${itemId}`;
  const response = await apiClient.put<{ success: boolean; id: number }>(
    endpoint,
    payload,
    token
  );

  if (!response.success) {
    return { success: false, error: response.error ?? 'Failed to update dashboard config' };
  }

  return { success: true, id: response.data?.id ?? itemId };
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export async function deleteDashboardItem(
  loginUserId: string | number,
  token: string,
  itemId: number
): Promise<MutateDashboardItemResponse> {
  const endpoint = `${base(loginUserId)}/${itemId}`;
  const response = await apiClient.delete<{ success: boolean }>(endpoint, token);

  if (!response.success) {
    return { success: false, error: response.error ?? 'Failed to delete dashboard config' };
  }

  return { success: true, id: itemId };
}

// ─── Get Single ───────────────────────────────────────────────────────────────

export async function getDashboardItem(
  loginUserId: string | number,
  token: string,
  itemId: number
): Promise<DashboardItem> {
  const response = await apiClient.get<DashboardItem>(
    `${base(loginUserId)}/${itemId}`,
    token
  );

  if (!response.success || !response.data) {
    throw new Error(response.error ?? 'getDashboardItem: Failed to fetch');
  }

  return response.data;
}

// ─── List ─────────────────────────────────────────────────────────────────────

export async function getDashboardItems(
  loginUserId: string | number,
  token: string,
  userType?: string
): Promise<DashboardItem[]> {
  const query = userType ? `?userType=${encodeURIComponent(userType)}` : '';
  const response = await apiClient.get<DashboardItem[]>(
    `${base(loginUserId)}${query}`,
    token
  );

  if (!response.success) {
    throw new Error(response.error ?? 'getDashboardItems: Failed to fetch');
  }

  return Array.isArray(response.data) ? response.data : [];
}
